import { useState, useEffect, useCallback } from "react";
import { getRegions, getDepartments, getIAs, getIEFs, resolveHierarchy } from "services/sdbService";

function pickId(row, ...keys) {
  for (const key of keys) {
    if (row?.[key] != null && row[key] !== "") return row[key];
  }
  return "";
}

function pickName(row, ...keys) {
  for (const key of keys) {
    if (row?.[key] != null && row[key] !== "") return String(row[key]);
  }
  return "";
}

export default function useHierarchySelects({
  open,
  initialDepartmentName = "",
  initialIefName = "",
}) {
  const [regions, setRegions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [ias, setIas] = useState([]);
  const [iefs, setIefs] = useState([]);

  const [selectedRegionId, setSelectedRegionId] = useState("");
  const [selectedDepartmentId, setSelectedDepartmentId] = useState("");
  const [selectedIaId, setSelectedIaId] = useState("");
  const [selectedIefId, setSelectedIefId] = useState("");

  const [departmentName, setDepartmentName] = useState("");
  const [iefName, setIefName] = useState("");
  const [loading, setLoading] = useState(false);

  const resetHierarchy = useCallback(() => {
    setDepartments([]);
    setIas([]);
    setIefs([]);
    setSelectedRegionId("");
    setSelectedDepartmentId("");
    setSelectedIaId("");
    setSelectedIefId("");
    setDepartmentName("");
    setIefName("");
  }, []);

  const applyIefSelection = useCallback((iefRow, deptRow) => {
    const deptName = pickName(deptRow, "departmentName", "DepartmentName");
    const ief = pickName(iefRow, "iefName", "IefName");
    setDepartmentName(deptName);
    setIefName(ief);
    return { state: deptName, district: ief };
  }, []);

  const loadIefsForSelection = useCallback(
    async (iaId, departmentId, deptRow, targetIefName) => {
      if (!iaId || !departmentId) {
        setIefs([]);
        setSelectedIefId("");
        setIefName("");
        return null;
      }
      const iefRows = await getIEFs(iaId, departmentId);
      const list = Array.isArray(iefRows) ? iefRows : [];
      setIefs(list);

      if (targetIefName) {
        const match = list.find(
          (row) => pickName(row, "iefName", "IefName").toLowerCase() === targetIefName.toLowerCase()
        );
        if (match) {
          const iefId = pickId(match, "iefId", "IefId");
          setSelectedIefId(iefId);
          return applyIefSelection(match, deptRow);
        }
      }

      setSelectedIefId("");
      setIefName("");
      return null;
    },
    [applyIefSelection]
  );

  const loadRegionChildren = useCallback(
    async (regionId, targetDepartmentName, targetIefName) => {
      if (!regionId) {
        setDepartments([]);
        setIas([]);
        setIefs([]);
        setSelectedDepartmentId("");
        setSelectedIaId("");
        setSelectedIefId("");
        setDepartmentName("");
        setIefName("");
        return null;
      }

      const [deptRows, iaRows] = await Promise.all([getDepartments(regionId), getIAs(regionId)]);
      const deptList = Array.isArray(deptRows) ? deptRows : [];
      const iaList = Array.isArray(iaRows) ? iaRows : [];
      setDepartments(deptList);
      setIas(iaList);

      let deptRow = null;
      let deptId = "";

      if (targetDepartmentName) {
        deptRow = deptList.find(
          (row) =>
            pickName(row, "departmentName", "DepartmentName").toLowerCase() ===
            targetDepartmentName.toLowerCase()
        );
        deptId = pickId(deptRow, "departmentId", "DepartmentId");
      }

      if (deptId) {
        setSelectedDepartmentId(deptId);
        setDepartmentName(pickName(deptRow, "departmentName", "DepartmentName"));

        if (iaList.length > 0) {
          if (targetIefName) {
            for (const iaRow of iaList) {
              const iaId = pickId(iaRow, "iaId", "IaId");
              const iefRows = await getIEFs(iaId, deptId);
              const iefList = Array.isArray(iefRows) ? iefRows : [];
              const iefMatch = iefList.find(
                (row) =>
                  pickName(row, "iefName", "IefName").toLowerCase() === targetIefName.toLowerCase()
              );
              if (iefMatch) {
                setSelectedIaId(iaId);
                setIefs(iefList);
                const iefId = pickId(iefMatch, "iefId", "IefId");
                setSelectedIefId(iefId);
                return applyIefSelection(iefMatch, deptRow);
              }
            }
          }

          const firstIaId = pickId(iaList[0], "iaId", "IaId");
          setSelectedIaId(firstIaId);
          return loadIefsForSelection(firstIaId, deptId, deptRow, targetIefName);
        }
      } else {
        setSelectedDepartmentId("");
        setSelectedIaId("");
        setIefs([]);
        setSelectedIefId("");
        setDepartmentName("");
        setIefName("");
      }

      return null;
    },
    [loadIefsForSelection]
  );

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const init = async () => {
      setLoading(true);
      resetHierarchy();
      try {
        if (initialDepartmentName) {
          const [regionRows, resolved] = await Promise.all([
            getRegions(),
            resolveHierarchy(initialDepartmentName, initialIefName),
          ]);
          if (cancelled) return;

          const regionList = Array.isArray(regionRows) ? regionRows : [];
          setRegions(regionList);

          if (resolved && resolved.regionId) {
            setSelectedRegionId(resolved.regionId);

            const [deptRows, iaRows] = await Promise.all([
              getDepartments(resolved.regionId),
              getIAs(resolved.regionId),
            ]);
            if (cancelled) return;

            const deptList = Array.isArray(deptRows) ? deptRows : [];
            const iaList = Array.isArray(iaRows) ? iaRows : [];
            setDepartments(deptList);
            setIas(iaList);

            if (resolved.departmentId) {
              setSelectedDepartmentId(resolved.departmentId);
              setDepartmentName(resolved.departmentName || "");
            }

            if (resolved.iaId) {
              setSelectedIaId(resolved.iaId);

              const iefRows = await getIEFs(resolved.iaId, resolved.departmentId);
              if (cancelled) return;
              const iefList = Array.isArray(iefRows) ? iefRows : [];
              setIefs(iefList);

              if (resolved.iefId) {
                setSelectedIefId(resolved.iefId);
                setIefName(resolved.iefName || "");
              }
            } else if (iaList.length > 0 && resolved.departmentId) {
              const firstIaId = pickId(iaList[0], "iaId", "IaId");
              setSelectedIaId(firstIaId);
              const iefRows = await getIEFs(firstIaId, resolved.departmentId);
              if (cancelled) return;
              setIefs(Array.isArray(iefRows) ? iefRows : []);
            }
          }
        } else {
          const regionRows = await getRegions();
          if (cancelled) return;
          setRegions(Array.isArray(regionRows) ? regionRows : []);
        }
      } catch (error) {
        console.error("Failed to load hierarchy:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [open, initialDepartmentName, initialIefName, resetHierarchy]);

  const handleRegionChange = async (regionId) => {
    setSelectedRegionId(regionId);
    setSelectedDepartmentId("");
    setSelectedIaId("");
    setSelectedIefId("");
    setDepartmentName("");
    setIefName("");
    await loadRegionChildren(regionId);
  };

  const handleDepartmentChange = async (departmentId) => {
    const deptRow = departments.find(
      (row) => pickId(row, "departmentId", "DepartmentId") === departmentId
    );
    setSelectedDepartmentId(departmentId);
    setDepartmentName(pickName(deptRow, "departmentName", "DepartmentName"));
    setSelectedIefId("");
    setIefName("");

    if (selectedIaId && departmentId) {
      await loadIefsForSelection(selectedIaId, departmentId, deptRow);
    } else {
      setIefs([]);
    }
  };

  const handleIaChange = async (iaId) => {
    setSelectedIaId(iaId);
    setSelectedIefId("");
    setIefName("");

    const deptRow = departments.find(
      (row) => pickId(row, "departmentId", "DepartmentId") === selectedDepartmentId
    );

    if (iaId && selectedDepartmentId) {
      await loadIefsForSelection(iaId, selectedDepartmentId, deptRow);
    } else {
      setIefs([]);
    }
  };

  const handleIefChange = (iefId) => {
    setSelectedIefId(iefId);
    const iefRow = iefs.find((row) => pickId(row, "iefId", "IefId") === iefId);
    const deptRow = departments.find(
      (row) => pickId(row, "departmentId", "DepartmentId") === selectedDepartmentId
    );
    if (iefRow && deptRow) {
      applyIefSelection(iefRow, deptRow);
    } else {
      setIefName("");
    }
  };

  return {
    regions,
    departments,
    ias,
    iefs,
    selectedRegionId,
    selectedDepartmentId,
    selectedIaId,
    selectedIefId,
    departmentName,
    iefName,
    loading,
    resetHierarchy,
    handleRegionChange,
    handleDepartmentChange,
    handleIaChange,
    handleIefChange,
  };
}
